import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { useToggle } from "react-use";
import { Collapse } from "../utils/Collapse";
import { Text } from "../text";
import { Button } from "../button";
import { styled } from "../theme";
/**
 * A component that displays a limited number of children and allows the user to expand to see the rest.
 *
 * @param {Props} props The props for the component.
 * @returns {JSX.Element} The rendered collapsible component.
 */
export const Collapsible = ({ children, splitIndex, openTitle, closeTitle, buttonSize, Button, }) => {
    const [isOpened, toggle] = useToggle(false);
    const { isCollapsible, visibleChildren, collapsedChildren } = splitChildrenByIndex({
        children,
        splitIndex,
    });
    return (_jsxs("div", { children: [visibleChildren, isCollapsible && collapsedChildren && (_jsx(Collapse, { isOpened: isOpened, children: collapsedChildren })), isCollapsible && (_jsx(Button, { onClick: toggle, size: buttonSize, type: "link", children: _jsx(Text, { fontWeight: "bold", children: isOpened ? closeTitle : openTitle }) }))] }));
};
Collapsible.defaultProps = {
    splitIndex: 1,
    Button: styled(Button) `
    && {
      height: auto;
      min-width: 0;
      margin-top: 8px;
    }
  `,
};
/**
 * Splits the children into visible and collapsed sections based on a split index.
 *
 * @param {object} params The parameters for splitting the children.
 * @param {ReactNode} params.children The children to split.
 * @param {number} params.splitIndex The index to split the children at.
 * @returns {object} An object containing the split children and a flag indicating if the children are collapsible.
 */
const splitChildrenByIndex = ({ children, splitIndex, }) => {
    const allChildren = React.Children.toArray(children);
    const topIndex = allChildren.length - 1;
    const isCollapsible = topIndex >= splitIndex;
    if (!isCollapsible) {
        return {
            isCollapsible,
            visibleChildren: allChildren,
        };
    }
    const lastVisibleChildIndex = isCollapsible ? splitIndex : topIndex;
    const visibleChildren = allChildren.slice(0, lastVisibleChildIndex);
    const collapsedChildren = allChildren.slice(lastVisibleChildIndex);
    return {
        isCollapsible,
        visibleChildren,
        collapsedChildren,
    };
};
